import { useState } from 'react'
import { Link } from 'wouter'
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextLink,
} from '@ideon-api/ideon-ui'
import { setHref } from '../../lib/account-links'
import type { CarrierEntry } from '../../lib/privileged-carriers'

const PAGE_SIZE = 20

const HEADING = 'font-manrope text-sm font-semibold text-ideon-gray-black dark:text-ideon-gray-30'

function StatesTable({ entry }: { entry: CarrierEntry }) {
  const [shown, setShown] = useState(PAGE_SIZE)
  const mapped = entry.stateIssuers ?? []
  // A state mapped only by blank-ID rows is absent from stateIssuers; merge it in, state order with the stateless row last.
  const rows = [
    ...mapped,
    ...entry.states.filter((s) => !mapped.some((m) => m.state === s)).map((state) => ({ state, issuerIds: [] })),
  ].sort((a, b) => (a.state === '' ? 1 : b.state === '' ? -1 : a.state.localeCompare(b.state)))
  const malformed = new Set(entry.malformedIssuerIds)

  if (rows.length === 0) return <p>—</p>

  return (
    <div className="flex flex-col gap-2">
      <Table variant="bordered" density="compact">
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Issuer IDs</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.slice(0, shown).map((row) => (
            <TableRow key={row.state}>
              <TableCell>{row.state || '—'}</TableCell>
              <TableCell>
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {row.issuerIds.length === 0 && '—'}
                  {row.issuerIds.map((id) =>
                    malformed.has(id) ? (
                      <Badge key={id} variant="error" dot={false} uppercase={false}>
                        {`${id} · malformed`}
                      </Badge>
                    ) : (
                      <span key={id} className="font-mono text-xs">
                        {id}
                      </span>
                    )
                  )}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between text-sm text-ideon-gray-70">
        <span>{`Showing ${Math.min(shown, rows.length)} of ${rows.length} states`}</span>
        {shown < rows.length && (
          <Button variant="text" onClick={() => setShown((n) => n + PAGE_SIZE)}>
            Show more
          </Button>
        )}
      </div>
    </div>
  )
}

export function CarrierDetail({ entry }: { entry: CarrierEntry }) {
  const wildcard = entry.grants.some((g) => g.accountSlug === null)
  const setGrants = entry.grants
    .filter((g): g is typeof g & { accountSlug: string } => g.accountSlug !== null)
    .sort((a, b) => a.accountSlug.localeCompare(b.accountSlug) || a.permissionSetKey.localeCompare(b.permissionSetKey))
  const count = setGrants.length + (wildcard ? 1 : 0)
  const ROW = 'flex items-center gap-3 border-t border-ideon-gray-40 px-4 py-3 text-sm first:border-t-0'

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className={HEADING}>States &amp; issuer IDs</h3>
        <StatesTable key={`${entry.productLine}:${entry.carrier}`} entry={entry} />
      </section>
      <section className="flex flex-col gap-2">
        <h3 className={HEADING}>{`Grants · ${count}`}</h3>
        {count === 0 ? (
          <p className="text-sm text-ideon-gray-70">No grants</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-ideon-gray-40 bg-white">
            {wildcard && (
              <div className={ROW}>
                <span className="font-semibold">All accounts (wildcard)</span>
                <Badge variant="neutral" dot={false} uppercase={false}>
                  Wildcard
                </Badge>
              </div>
            )}
            {setGrants.map((g) => (
              <div key={`${g.accountSlug}/${g.permissionSetKey}`} className={ROW}>
                <span className="font-semibold">{g.accountSlug}</span>
                <TextLink asChild>
                  <Link href={setHref(g.accountSlug, 'privileged', g.permissionSetKey)}>{g.permissionSetKey}</Link>
                </TextLink>
                <Badge variant="neutral" dot={false} uppercase={false}>
                  Override
                </Badge>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
